import { useState } from 'react'

import { type SetLearnerPinResult } from '@/platform/access/learner-session'
import { PIN_MAX_LENGTH, PIN_MIN_LENGTH } from '@/platform/family/api'
import { toAppError } from '@/shared/errors/envelope'
import { PinPad } from '@/shared/ui/PinPad'

import { formatSeconds, useCountdown } from './countdown'
import { LearnerAvatar } from './entry-ui'

/**
 * Screen B (D-16): the parent sets or resets a learner's PIN. Three steps on
 * the same numeric pad: new PIN, the same PIN again, then the parent PIN as
 * step-up confirmation (`set_learner_pin` checks it server-side). Resetting
 * ends the learner's open sessions.
 */

export interface LearnerPinSetupScreenProps {
  readonly learner: { readonly id: string; readonly nickname: string }
  /** The learner already has a PIN: this is a reset. */
  readonly reset: boolean
  readonly onSave: (newPin: string, parentPin: string) => Promise<SetLearnerPinResult>
  readonly onDone: () => void
  readonly onCancel: () => void
  readonly cancelLabel?: string
  readonly now?: () => number
}

type Step =
  { kind: 'new' } | { kind: 'confirm'; pin: string } | { kind: 'parent'; pin: string }

export function LearnerPinSetupScreen({
  learner,
  reset,
  onSave,
  onDone,
  onCancel,
  cancelLabel = 'Hủy',
  now = Date.now,
}: LearnerPinSetupScreenProps): React.JSX.Element {
  const [step, setStep] = useState<Step>({ kind: 'new' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lockedUntil, setLockedUntil] = useState<string | null>(null)
  const lockSeconds = useCountdown(lockedUntil, now)

  const save = async (newPin: string, parentPin: string) => {
    setBusy(true)
    setError(null)
    try {
      const result = await onSave(newPin, parentPin)
      if (result.configured) {
        onDone()
        return
      }
      if (result.locked_until !== null) setLockedUntil(result.locked_until)
      else
        setError(
          `PIN phụ huynh chưa đúng. Còn ${String(result.attempts_remaining)} lần thử.`,
        )
    } catch (e) {
      const code = toAppError(e).code
      setError(
        code === 'SESSION_EXPIRED'
          ? 'Phiên đăng nhập đã hết hạn.'
          : code === 'INVALID_INPUT'
            ? 'Mã PIN của bé cần có 4–6 chữ số.'
            : 'Chưa lưu được mã PIN. Vui lòng thử lại.',
      )
    } finally {
      setBusy(false)
    }
  }

  const titles = {
    new: `Mã PIN mới cho ${learner.nickname}`,
    confirm: 'Nhập lại mã PIN để kiểm tra',
    parent: 'Nhập PIN phụ huynh để xác nhận',
  } as const

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col gap-5 px-6 pt-4 pb-10">
      <div className="flex flex-col items-center gap-2 text-center">
        <LearnerAvatar id={learner.id} nickname={learner.nickname} />
        <h1 className="text-2xl font-bold">
          {reset ? 'Đổi mã PIN của bé' : 'Đặt mã PIN cho bé'}
        </h1>
        <p className="text-slate-700 dark:text-slate-300">
          Bé dùng mã này để vào học trên màn hình chọn người học. Mã có 4–6 chữ số.
          {reset && ' Đổi mã sẽ đăng xuất bé khỏi các thiết bị đang học.'}
        </p>
      </div>

      {lockSeconds > 0 ? (
        <div className="text-center" role="alert">
          <p className="text-lg font-semibold">Đã nhập sai PIN phụ huynh 5 lần</p>
          <p className="mt-1 text-slate-700 dark:text-slate-300">
            Thử lại sau {formatSeconds(lockSeconds)}.
          </p>
          <button
            type="button"
            className="mt-5 px-4 text-slate-600 underline dark:text-slate-400"
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
        </div>
      ) : (
        <PinPad
          key={step.kind}
          title={titles[step.kind]}
          error={error}
          minLength={PIN_MIN_LENGTH}
          maxLength={PIN_MAX_LENGTH}
          busy={busy}
          submitLabel={step.kind === 'parent' ? 'Lưu mã PIN' : 'Tiếp tục'}
          onSubmit={(pin) => {
            setError(null)
            if (step.kind === 'new') setStep({ kind: 'confirm', pin })
            else if (step.kind === 'confirm') {
              if (pin === step.pin) setStep({ kind: 'parent', pin })
              else {
                setStep({ kind: 'new' })
                setError('Hai lần nhập chưa giống nhau. Vui lòng đặt lại mã PIN.')
              }
            } else void save(step.pin, pin)
          }}
          onCancel={onCancel}
        />
      )}
    </main>
  )
}
